import React, { useEffect } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "./components/Navbar";
import { ROUTES } from "./config/constants";
import ProtectedRoute from "./components/ProtectedRoute";
import SubscriptionRoute from "./components/SubscriptionRoute";
import { fetchCurrentUser } from "./store/authSlice";
import { fetchOrganizationContext } from "./store/organizationContextSlice";
import { fetchUnreadNotificationCount } from "./store/notificationsSlice";

// Pages
import HomePage from "./pages/HomePage";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import SearchLeads from "./pages/SearchLeads";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import OrganizationContext from "./pages/OrganizationContext";
import Campaigns from "./pages/Campaigns";
import Products from "./pages/Products";
import ConnectionsPage from "./pages/ConnectionsPage";
import ManageSubscription from "./pages/ManageSubscription";
import Guides from "./pages/Guides";
import UnsubscribePage from "./pages/UnsubscribePage";

const UNREAD_COUNT_REFRESH_INTERVAL_MS = 60000;
const UNREAD_COUNT_FOCUS_THROTTLE_MS = 5000;

function App() {
    const dispatch = useDispatch();
    const { currentUserStatus, isAuthenticated, token } = useSelector(
        (state) => state.auth,
    );
    const organizationContextStatus = useSelector(
        (state) => state.organizationContext.status,
    );
    const authenticatedRedirect = ROUTES.DASHBOARD;

    useEffect(() => {
        if (isAuthenticated && token && currentUserStatus === "idle") {
            dispatch(fetchCurrentUser());
        }
    }, [currentUserStatus, dispatch, isAuthenticated, token]);

    useEffect(() => {
        if (isAuthenticated && token && organizationContextStatus === "idle") {
            dispatch(fetchOrganizationContext());
        }
    }, [dispatch, isAuthenticated, organizationContextStatus, token]);

    useEffect(() => {
        if (!isAuthenticated || !token) return undefined;

        let lastRefreshAt = 0;

        const refreshUnreadCount = () => {
            lastRefreshAt = Date.now();
            dispatch(fetchUnreadNotificationCount());
        };

        const handleWindowFocus = () => {
            if (Date.now() - lastRefreshAt < UNREAD_COUNT_FOCUS_THROTTLE_MS) {
                return;
            }

            refreshUnreadCount();
        };

        refreshUnreadCount();

        window.addEventListener("focus", handleWindowFocus);
        const intervalId = window.setInterval(
            refreshUnreadCount,
            UNREAD_COUNT_REFRESH_INTERVAL_MS,
        );

        return () => {
            window.removeEventListener("focus", handleWindowFocus);
            window.clearInterval(intervalId);
        };
    }, [dispatch, isAuthenticated, token]);

    return (
        <Router>
            <Navbar />
            <ToastContainer position="top-right" autoClose={3000} />

            <Routes>
                {/* Public Routes */}
                <Route path={ROUTES.LANDING_PAGE} element={<HomePage />} />
                <Route path={ROUTES.HOME} element={<HomePage />} />
                <Route
                    path={ROUTES.LOGIN}
                    element={
                        !isAuthenticated ? (
                            <Login />
                        ) : (
                            <Navigate to={authenticatedRedirect} replace />
                        )
                    }
                />
                <Route
                    path={ROUTES.SIGNUP}
                    element={
                        !isAuthenticated ? (
                            <Signup />
                        ) : (
                            <Navigate to={authenticatedRedirect} replace />
                        )
                    }
                />
                <Route
                    path={ROUTES.FORGOT_PASSWORD}
                    element={
                        !isAuthenticated ? (
                            <ForgotPassword />
                        ) : (
                            <Navigate to={authenticatedRedirect} replace />
                        )
                    }
                />
                <Route
                    path={ROUTES.RESET_PASSWORD}
                    element={<ResetPassword />}
                />
                <Route
                    path={ROUTES.MANAGE_SUBSCRIPTION}
                    element={<ManageSubscription />}
                />
                <Route
                    path={ROUTES.VERIFY_EMAIL}
                    element={
                        !isAuthenticated ? (
                            <VerifyEmail />
                        ) : (
                            <Navigate to={authenticatedRedirect} replace />
                        )
                    }
                />
                {/* LGS-008 T9 — public, unauthenticated React route for
                    the unsubscribe confirmation. Reaches the BE handler
                    via API_ENDPOINTS.UNSUBSCRIBE on mount. Mounted at
                    /unsubscribe/:orgId/:leadId and reachable directly
                    from email clients without auth. (2026-10-17) */}
                <Route
                    path={ROUTES.UNSUBSCRIBE_PATTERN}
                    element={<UnsubscribePage />}
                />

                {/* Logged In Routes */}
                <Route element={<ProtectedRoute />}>
                    <Route path={ROUTES.PROFILE} element={<Profile />} />
                    <Route
                        path={ROUTES.ORGANIZATION_CONTEXT}
                        element={<OrganizationContext />}
                    />

                    {/* Active Subscription Routes */}
                    <Route element={<SubscriptionRoute />}>
                        <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
                        <Route path={ROUTES.SEARCH} element={<SearchLeads />} />
                        <Route path={ROUTES.PRODUCTS} element={<Products />} />
                        <Route path={ROUTES.CAMPAIGNS} element={<Campaigns />} />
                        <Route
                            path={ROUTES.CONNECTIONS}
                            element={<ConnectionsPage />}
                        />
                        <Route path={ROUTES.GUIDES} element={<Guides />} />
                    </Route>
                </Route>
            </Routes>
        </Router>
    );
}

export default App;
